import type { Awaitable } from '@ipfabric/ui-components/utils/types/Awaitable';
import type { GlobalStateCommandFactory } from 'components/GlobalState/GlobalStateModel';
import { initializeAuthentication } from 'components/GlobalState/commands/initializeAuthentication';
import { initializeSSOAuthentication } from 'components/GlobalState/commands/initializeSSOAuthentication';
import { loadInitialAppData } from 'components/GlobalState/commands/loadInitialAppData';
import { setAppLoadError } from 'components/GlobalState/commands/setAppLoadError';
import { setSystemUnavailable } from 'components/GlobalState/commands/setSystemUnavailable';
import { verifyLicense } from 'components/GlobalState/commands/verifyLicense';
import { selectAppInitialized } from 'components/GlobalState/selectors/selectAppInitialized';
import { selectLoadedInitialAppData } from 'components/GlobalState/selectors/selectLoadedInitialAppData';

/**
 * Perform all requests needed to initialize the app
 *
 * For each request it is checked whether it was already loaded,
 * because if one or more fail due to unavailable API,
 * this command is retried after API is available again.
 *
 * An exception is thrown outside only in case that some of these
 * requests end with an unexpected error, other than unavailable API.
 */
export const initializeApp: GlobalStateCommandFactory =
  (cmdContext, dispatch) => async () => {
    try {
      const state = cmdContext.getState();

      // Decide which authentication initialization to run
      let initializeAuthPromise: Awaitable<void>;
      if (state.loginStatus !== 'UNINITIALIZED') {
        initializeAuthPromise = Promise.resolve();
      } else if (state.location.pathname === '/auth-callback') {
        initializeAuthPromise = dispatch(initializeSSOAuthentication);
      } else {
        initializeAuthPromise = dispatch(initializeAuthentication);
      }

      await Promise.all([
        selectLoadedInitialAppData(state)
          ? Promise.resolve()
          : dispatch(loadInitialAppData),
        state.license.state !== 'NOT_VERIFIED'
          ? Promise.resolve()
          : dispatch(verifyLicense),
        initializeAuthPromise,
      ]);

      // Requests failing before initialization set `systemUnavailable`, but SystemUnavailableOverlay,
      // which resets it, is mounted only after initialization. Initialization has just succeeded,
      // so the API is available. Later runs (after socket reconnect) leave the reset to the overlay.
      if (
        !selectAppInitialized(state) &&
        selectAppInitialized(cmdContext.getState())
      ) {
        dispatch(setSystemUnavailable, { value: false });
      }
    } catch (e) {
      // when unknown error happens during initial requests, exit initial loader
      // so AppInitializer will show just a simple irrecoverable error.
      dispatch(setAppLoadError);
      throw e;
    }
  };
